<template>
    <div
        class="rounded-xl border border-theme-border bg-theme-card overflow-hidden"
    >
        <div class="border-b border-theme-border px-5 py-4">
            <div class="flex items-center justify-between gap-3">
                <h2 class="text-lg font-semibold text-theme-base">
                    Vista previa del catálogo
                </h2>

                <router-link
                    v-if="canEditCatalog"
                    :to="{ name: 'CatalogEditor' }"
                    class="flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-theme-border bg-theme-card px-3 py-1.5 text-sm font-medium text-theme-primary transition hover:bg-theme-hover"
                >
                    Editar catálogo
                </router-link>
            </div>
        </div>

        <div v-if="isLoading" class="p-5">
            <div
                class="w-full h-105 rounded-2xl bg-theme-hover flex flex-col items-center justify-center gap-4 text-sm text-theme-muted"
            >
                <tag-icon
                    class="w-12 h-12 text-stack-primary origin-tag-eyelet animate-tag-swing"
                />
                Cargando tu catálogo...
            </div>
        </div>

        <div v-else-if="hasError" class="p-5">
            <div
                class="w-full h-105 rounded-2xl border border-dashed border-theme-border flex flex-col items-center justify-center gap-3 text-center text-sm text-theme-muted p-6"
            >
                No se pudo cargar la vista previa.
                <button
                    type="button"
                    class="rounded-lg border border-theme-border px-3 py-1.5 text-xs font-medium text-theme-base transition hover:bg-theme-hover"
                    @click="loadCatalog"
                >
                    Reintentar
                </button>
            </div>
        </div>
        <template v-else>
            <div
                class="relative"
                :class="isClipped ? 'overflow-hidden' : ''"
                :style="
                    isClipped ? { maxHeight: `${collapsedHeight}px` } : null
                "
            >
                <div
                    ref="previewContent"
                    class="flex justify-center animate-fade-in-up"
                >
                    <catalog-preview-frame :catalog="catalog || {}" />
                </div>
                <div
                    v-if="isClipped"
                    class="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-theme-card to-transparent"
                ></div>
            </div>
            <div v-if="shouldCollapse" class="flex justify-center px-5 py-2">
                <button
                    type="button"
                    class="flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium text-theme-primary transition hover:bg-theme-hover"
                    @click="isExpanded = !isExpanded"
                >
                    {{ isExpanded ? "Ver menos" : "Ver más" }}
                    <component
                        :is="
                            isExpanded ? 'chevron-up-icon' : 'chevron-down-icon'
                        "
                        class="h-4 w-4"
                    />
                </button>
            </div>
        </template>

        <div
            class="flex items-center gap-2 border-t border-theme-border px-5 py-3"
        >
            <a
                :href="catalogUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="flex min-w-0 flex-1 items-center gap-2 text-sm text-theme-primary hover:underline"
            >
                <link-icon class="h-4 w-4 flex-shrink-0" />
                <span class="truncate">{{ catalogUrl }}</span>
            </a>

            <app-button
                v-if="showQr"
                icon
                border
                aria-label="Ver código QR"
                @click="isQrDialogOpen = true"
            >
                <qr-code-icon class="h-4 w-4" />
            </app-button>

            <app-button icon border @click="copyLink">
                <copy-icon class="h-4 w-4" />
            </app-button>
        </div>

        <app-dialog v-if="showQr" v-model="isQrDialogOpen" size="sm">
            <template #header>
                <h3 class="text-lg font-semibold text-theme-base">
                    Código QR de tu página
                </h3>
            </template>
            <div class="flex flex-col items-center gap-3">
                <div class="rounded-lg bg-white p-2">
                    <qr-viewer :value="catalogUrl" :size="240" />
                </div>
                <p class="break-all text-center text-sm text-theme-muted">
                    {{ catalogUrl }}
                </p>
            </div>
            <template #footer>
                <app-button block relief @click="downloadQr">
                    Descargar
                </app-button>
            </template>
        </app-dialog>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";

import { IS_ENABLED_CATALOG_QR } from "@/common/constants";
import { buildQrFilename, downloadQrImage } from "@/common/lib/qr";

import CatalogPreviewFrame from "@/components/editor/CatalogPreviewFrame.vue";
import ChevronDownIcon from "@/components/icons/ChevronDownIcon.vue";
import ChevronUpIcon from "@/components/icons/ChevronUpIcon.vue";
import CopyIcon from "@/components/icons/CopyIcon.vue";
import LinkIcon from "@/components/icons/LinkIcon.vue";
import QrCodeIcon from "@/components/icons/QrCodeIcon.vue";
import TagIcon from "@/components/icons/TagIcon.vue";
import AppDialog from "@/components/utils/AppDialog.vue";
import QrViewer from "@/components/utils/QrViewer.vue";

// Con `collapsible`, la tarjeta completa queda casi del alto de Herramientas
// en computador. Si la vista previa se pasa por menos de la tolerancia se
// muestra completa: un "Ver más" que revela unos pocos píxeles no aporta.
const COLLAPSED_HEIGHT_PX = 520;
const EXPAND_THRESHOLD_PX = 120;

// Mismo tamaño de primera página que ProductsSearch en modo `paginated`
// (ver openspec/changes/paginacion-productos): es el default ya establecido
// para listProductsPaginated, no uno nuevo inventado para este preview.
const PAGE_SIZE = 12;

// Vistas que muestran productos en el catálogo público.
const PRODUCT_DISPLAY_MODES = ["catalog", "mix"];

const CATALOG_PREVIEW_CACHE_TTL_MS = 5 * 60 * 1000;

export default {
    name: "CatalogPreviewCard",
    components: {
        AppDialog,
        ChevronDownIcon,
        ChevronUpIcon,
        CopyIcon,
        LinkIcon,
        QrCodeIcon,
        TagIcon,
        CatalogPreviewFrame,
        QrViewer
    },
    props: {
        collapsible: {
            type: Boolean,
            default: false
        },
        showQrButton: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            catalog: null,
            isLoading: false,
            hasError: false,
            previewHeight: 0,
            isExpanded: false,
            isQrDialogOpen: false,
            collapsedHeight: COLLAPSED_HEIGHT_PX
        };
    },
    computed: {
        ...mapState("tenants", ["catalogPreview"]),
        ...mapState("auth", ["tenantId", "tenantSlug"]),
        shouldCollapse() {
            return (
                this.collapsible &&
                this.previewHeight > COLLAPSED_HEIGHT_PX + EXPAND_THRESHOLD_PX
            );
        },
        isClipped() {
            return this.shouldCollapse && !this.isExpanded;
        },
        showQr() {
            return (
                this.showQrButton &&
                IS_ENABLED_CATALOG_QR &&
                Boolean(this.catalogUrl)
            );
        },
        ...mapGetters("tenants", ["isCatalogPreviewCacheValid"]),
        canEditCatalog() {
            return this.$ability.hasPermission("tenant:GetBrandingSettings");
        },
        catalogUrl() {
            return this.$publicUrl;
        }
    },
    watch: {
        isLoading() {
            this.$nextTick(this.observePreview);
        },
        hasError() {
            this.$nextTick(this.observePreview);
        }
    },
    mounted() {
        this.loadCatalog();
        this.$nextTick(this.observePreview);
    },
    beforeDestroy() {
        this.resizeObserver?.disconnect();
    },
    methods: {
        // El alto cambia mientras cargan productos e imágenes, por eso se
        // observa en vivo en vez de medir una sola vez.
        observePreview() {
            if (!this.collapsible) return;
            this.resizeObserver?.disconnect();
            const element = this.$refs.previewContent;
            if (!element) return;
            this.resizeObserver = new ResizeObserver(([entry]) => {
                this.previewHeight = entry.contentRect.height;
            });
            this.resizeObserver.observe(element);
        },
        async downloadQr() {
            try {
                await downloadQrImage(
                    this.catalogUrl,
                    buildQrFilename(this.catalogUrl, this.tenantSlug)
                );
            } catch (error) {
                this.$swal.fire({
                    icon: "error",
                    title: "No pudimos generar la imagen",
                    text: error?.message || "Intenta de nuevo en unos segundos."
                });
            }
        },
        ...mapActions("tenants", ["getCatalogPreview"]),
        ...mapActions("products", ["listProductsPaginated"]),
        ...mapMutations("tenants", ["SET_CATALOG_PREVIEW"]),
        async loadCatalog() {
            if (this.isCatalogPreviewCacheValid) {
                this.catalog = this.catalogPreview.data;
                return;
            }
            this.isLoading = true;
            this.hasError = false;
            try {
                const catalogData = await this.getCatalogPreview();
                const products = await this.loadPreviewProducts(
                    catalogData.displayMode
                );
                this.catalog = { ...catalogData, products };
                this.SET_CATALOG_PREVIEW({
                    data: this.catalog,
                    tenantId: this.tenantId,
                    expiredAt: Date.now() + CATALOG_PREVIEW_CACHE_TTL_MS
                });
            } catch {
                this.hasError = true;
            } finally {
                this.isLoading = false;
            }
        },
        // Refleja lo que ve el cliente: sin productos en `feed`/`segments`.
        // Si la consulta falla (ej. plan sin productos) la vista previa sale
        // igual, sin productos.
        async loadPreviewProducts(displayMode) {
            if (!PRODUCT_DISPLAY_MODES.includes(displayMode)) return [];
            try {
                const { items } = await this.listProductsPaginated({
                    active: true,
                    page: 1,
                    itemsByPage: PAGE_SIZE
                });
                return items;
            } catch {
                return [];
            }
        },
        async copyLink() {
            await navigator.clipboard.writeText(this.catalogUrl);
            this.$toast.success({
                title: "Enlace copiado",
                text: "El enlace del catálogo se copió al portapapeles."
            });
        }
    }
};
</script>
